import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  Grid,
  Button,
  Skeleton,
  Alert,
  AlertTitle,
  alpha,
  useTheme,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  CircularProgress,
  Stack,
} from '@mui/material';
import {
  History,
  TrendingUp,
  TrendingDown,
  Refresh,
  Search,
  Receipt,
  Visibility,
  ContentCopy,
  QrCode,
  Cancel,
  CheckCircle,
  Download,
  Close,
} from '@mui/icons-material';
import { api } from '@/services/api';
import toast from 'react-hot-toast';
import { QRCodeSVG } from 'qrcode.react';

interface PixPayment {
  endToEndId: string;
  txid?: string;
  valor: string;
  horario: string;
  pagador?: {
    nome?: string;
    cpf?: string;
    cnpj?: string;
  };
  infoPagador?: string;
}

interface PixCharge {
  txid: string;
  status: string;
  revisao?: number;
  valor: {
    original: string;
    modalidadeAlteracao?: number;
  };
  calendario?: {
    criacao: string;
    expiracao: number;
  };
  devedor?: {
    nome?: string;
    cpf?: string;
    cnpj?: string;
  };
  chave?: string;
  solicitacaoPagador?: string;
  pixCopiaECola?: string;
  brcode?: string;
  location?: string;
  loc?: {
    id: number;
    location: string;
    tipoCob: string;
  };
  infoAdicionais?: Array<{ nome: string; valor: string }>;
}

// Helper to format date as YYYY-MM-DD in local timezone
const formatLocalDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Calculate max end date based on start date (7 days max, same month)
const getMaxEndDate = (start: string): string => {
  const startDate = new Date(start + 'T12:00:00');
  const sevenDaysLater = new Date(startDate);
  sevenDaysLater.setDate(sevenDaysLater.getDate() + 6);

  // Get last day of the same month
  const lastDayOfMonth = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0);

  // Return the earlier of: 7 days later, last day of month, or today
  const today = new Date();
  const candidates = [sevenDaysLater, lastDayOfMonth, today];
  const minDate = candidates.reduce((min, d) => (d < min ? d : min));

  return formatLocalDate(minDate);
};

// Calculate min start date based on end date (7 days max, same month)
const getMinStartDate = (end: string): string => {
  const endDate = new Date(end + 'T12:00:00');
  const sevenDaysEarlier = new Date(endDate);
  sevenDaysEarlier.setDate(sevenDaysEarlier.getDate() - 6);

  // Get first day of the same month
  const firstDayOfMonth = new Date(endDate.getFullYear(), endDate.getMonth(), 1);

  // Return the later of: 7 days earlier or first day of month
  return formatLocalDate(sevenDaysEarlier > firstDayOfMonth ? sevenDaysEarlier : firstDayOfMonth);
};

const PixHistoryPage: React.FC = () => {
  const theme = useTheme();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [payments, setPayments] = useState<PixPayment[]>([]);
  const [charges, setCharges] = useState<PixCharge[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  // Default to last 6 days (7 days total including today)
  const today = formatLocalDate(new Date());
  const sixDaysAgo = (() => {
    const d = new Date();
    d.setDate(d.getDate() - 6);
    return formatLocalDate(d);
  })();

  const [startDate, setStartDate] = useState(sixDaysAgo);
  const [endDate, setEndDate] = useState(today);
  const [search, setSearch] = useState('');

  // Handle start date change - auto-adjust end date if needed
  const handleStartDateChange = (newStart: string) => {
    setStartDate(newStart);
    const maxEnd = getMaxEndDate(newStart);
    if (endDate > maxEnd) {
      setEndDate(maxEnd);
    }
    // Also check if end date is in a different month
    const startMonth = new Date(newStart + 'T12:00:00').getMonth();
    const endMonth = new Date(endDate + 'T12:00:00').getMonth();
    if (startMonth !== endMonth) {
      setEndDate(maxEnd);
    }
  };

  // Handle end date change - auto-adjust start date if needed
  const handleEndDateChange = (newEnd: string) => {
    setEndDate(newEnd);
    const minStart = getMinStartDate(newEnd);
    if (startDate < minStart) {
      setStartDate(minStart);
    }
    // Also check if start date is in a different month
    const startMonth = new Date(startDate + 'T12:00:00').getMonth();
    const endMonth = new Date(newEnd + 'T12:00:00').getMonth();
    if (startMonth !== endMonth) {
      setStartDate(minStart);
    }
  };

  // Modal states
  const [selectedCharge, setSelectedCharge] = useState<PixCharge | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [copied, setCopied] = useState(false);

  // Only reload when user switches tabs if they have already searched
  useEffect(() => {
    if (hasSearched) {
      loadData();
    }
  }, [tab]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    setHasSearched(true);

    try {
      // Use local dates with proper timezone handling
      const startISO = new Date(startDate + 'T00:00:00').toISOString();
      const endISO = new Date(endDate + 'T23:59:59').toISOString();

      if (tab === 0) {
        const response = await api.get('/pix/payments', {
          params: {
            inicio: startISO,
            fim: endISO,
          },
        });

        if (response?.success) {
          setPayments(response.data || []);
        } else {
          setError(response?.error?.message || 'Erro ao carregar pagamentos');
        }
      } else {
        const response = await api.get('/pix/charges', {
          params: {
            inicio: startISO,
            fim: endISO,
          },
        });

        if (response?.success) {
          setCharges(response.data || []);
        } else {
          setError(response?.error?.message || 'Erro ao carregar cobranças');
        }
      }
    } catch (err: any) {
      console.error('Error loading data:', err);
      setError(err.message || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    loadData();
  };

  const handleViewDetails = async (charge: PixCharge) => {
    setDetailsOpen(true);
    setLoadingDetails(true);
    setSelectedCharge(charge);

    try {
      const response = await api.get(`/pix/charges/${charge.txid}`);
      if (response?.success) {
        setSelectedCharge(response.data);
      }
    } catch (err) {
      console.error('Error loading charge details:', err);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleCancelCharge = async () => {
    if (!selectedCharge) return;

    setCanceling(true);
    try {
      const response = await api.delete(`/pix/charges/${selectedCharge.txid}`);
      if (response?.success) {
        toast.success('Cobrança cancelada com sucesso');
        setDetailsOpen(false);
        loadData();
      } else {
        toast.error(response?.error?.message || 'Erro ao cancelar cobrança');
      }
    } catch (err: any) {
      toast.error(err.message || 'Erro ao cancelar cobrança');
    } finally {
      setCanceling(false);
    }
  };

  const handleCopyPixCode = () => {
    const code = selectedCharge?.pixCopiaECola || selectedCharge?.brcode;
    if (code) {
      navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success('Código PIX copiado!');
      setTimeout(() => setCopied(false), 3000);
    }
  };

  const handleDownloadQRCode = () => {
    const svg = document.getElementById('qr-code-svg');
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx?.drawImage(img, 0, 0);
      const pngFile = canvas.toDataURL('image/png');

      const downloadLink = document.createElement('a');
      downloadLink.download = `qrcode-${selectedCharge?.txid}.png`;
      downloadLink.href = pngFile;
      downloadLink.click();
    };

    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const formatCurrency = (value: string | number) => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleString('pt-BR');
    } catch {
      return dateString;
    }
  };

  const formatExpiracao = (segundos: number) => {
    if (segundos < 60) return `${segundos} segundos`;
    if (segundos < 3600) return `${Math.floor(segundos / 60)} minutos`;
    if (segundos < 86400) return `${Math.floor(segundos / 3600)} horas`;
    return `${Math.floor(segundos / 86400)} dias`;
  };

  const getStatusChip = (status: string) => {
    const statusConfig: Record<string, { color: 'success' | 'warning' | 'error' | 'info' | 'default'; label: string }> = {
      CONCLUIDA: { color: 'success', label: 'Concluída' },
      ATIVA: { color: 'info', label: 'Ativa' },
      REMOVIDA_PELO_USUARIO_RECEBEDOR: { color: 'default', label: 'Cancelada' },
      REMOVIDA_PELO_PSP: { color: 'error', label: 'Removida' },
    };

    const config = statusConfig[status] || { color: 'default', label: status };
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const maskDocument = (cpf?: string, cnpj?: string) => {
    if (cpf) {
      return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '***.$2.***-**');
    }
    if (cnpj) {
      return cnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '**.$2.$3/****-**');
    }
    return '-';
  };

  const filteredPayments = payments.filter((p) => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      p.pagador?.nome?.toLowerCase().includes(searchLower) ||
      p.endToEndId.toLowerCase().includes(searchLower) ||
      p.infoPagador?.toLowerCase().includes(searchLower)
    );
  });

  const filteredCharges = charges.filter((c) => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      c.devedor?.nome?.toLowerCase().includes(searchLower) ||
      c.txid.toLowerCase().includes(searchLower) ||
      c.solicitacaoPagador?.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <Box>
        <Skeleton variant="text" width={300} height={40} />
        <Skeleton variant="rounded" height={80} sx={{ my: 2 }} />
        <Skeleton variant="rounded" height={400} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">
          <History sx={{ mr: 1, verticalAlign: 'middle' }} />
          Histórico PIX
        </Typography>
        <Button variant="outlined" startIcon={<Refresh />} onClick={loadData}>
          Atualizar
        </Button>
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab icon={<TrendingUp />} iconPosition="start" label="Recebimentos" />
          <Tab icon={<Receipt />} iconPosition="start" label="Cobranças" />
        </Tabs>
      </Paper>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Data Início"
              type="date"
              value={startDate}
              onChange={(e) => handleStartDateChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                max: endDate,
              }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Data Fim"
              type="date"
              value={endDate}
              onChange={(e) => handleEndDateChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: startDate,
                max: today,
              }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Buscar"
              placeholder="Nome, documento, TXID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button
              variant="contained"
              fullWidth
              startIcon={<Search />}
              onClick={handleSearch}
            >
              Buscar
            </Button>
          </Grid>
        </Grid>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Máximo de 7 dias, dentro do mesmo mês (limitação da API Sicoob)
        </Typography>
      </Paper>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={loadData}>
              Tentar Novamente
            </Button>
          }
        >
          <AlertTitle>Erro ao carregar dados</AlertTitle>
          {error}
        </Alert>
      )}

      {tab === 0 ? (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Tipo</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Pagador</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Documento</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Valor</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Data/Hora</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>ID</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPayments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <TrendingUp sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography color="text.secondary">
                      {hasSearched ? 'Nenhum pagamento encontrado no período' : 'Clique em "Buscar" para carregar os pagamentos'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredPayments.map((payment) => (
                  <TableRow key={payment.endToEndId} hover>
                    <TableCell>
                      <Chip
                        icon={<TrendingUp sx={{ fontSize: 14 }} />}
                        label="Recebido"
                        size="small"
                        sx={{
                          bgcolor: alpha(theme.palette.success.main, 0.12),
                          color: 'success.main',
                          '& .MuiChip-icon': { color: 'success.main' },
                        }}
                      />
                    </TableCell>
                    <TableCell>{payment.pagador?.nome || payment.infoPagador || '-'}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {maskDocument(payment.pagador?.cpf, payment.pagador?.cnpj)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                      +{formatCurrency(payment.valor)}
                    </TableCell>
                    <TableCell>{formatDate(payment.horario)}</TableCell>
                    <TableCell>
                      <Tooltip title={payment.endToEndId}>
                        <Typography variant="body2" fontFamily="monospace" noWrap sx={{ maxWidth: 120 }}>
                          {payment.endToEndId.substring(0, 12)}...
                        </Typography>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>TXID</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Devedor</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Descrição</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Valor</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Criação</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCharges.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Receipt sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography color="text.secondary">
                      {hasSearched ? 'Nenhuma cobrança encontrada no período' : 'Clique em "Buscar" para carregar as cobranças'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredCharges.map((charge) => (
                  <TableRow key={charge.txid} hover>
                    <TableCell>
                      <Tooltip title={charge.txid}>
                        <Typography variant="body2" fontFamily="monospace">
                          {charge.txid.substring(0, 15)}...
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{charge.devedor?.nome || '-'}</TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {charge.solicitacaoPagador || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {formatCurrency(charge.valor.original)}
                    </TableCell>
                    <TableCell>
                      {getStatusChip(charge.status)}
                    </TableCell>
                    <TableCell>
                      {charge.calendario?.criacao ? formatDate(charge.calendario.criacao) : '-'}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Ver detalhes">
                        <IconButton size="small" onClick={() => handleViewDetails(charge)}>
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Charge Details Modal */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Receipt />
            Detalhes da Cobrança
          </Box>
          <IconButton onClick={() => setDetailsOpen(false)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {loadingDetails ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : selectedCharge ? (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">TXID</Typography>
                    <Typography variant="body2" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>
                      {selectedCharge.txid}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">Status</Typography>
                    <Box sx={{ mt: 0.5 }}>{getStatusChip(selectedCharge.status)}</Box>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">Valor</Typography>
                    <Typography variant="h5" fontWeight="bold" color="primary">
                      {formatCurrency(selectedCharge.valor.original)}
                    </Typography>
                  </Box>

                  {selectedCharge.chave && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Chave PIX</Typography>
                      <Typography variant="body2" fontFamily="monospace">
                        {selectedCharge.chave}
                      </Typography>
                    </Box>
                  )}

                  {selectedCharge.solicitacaoPagador && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Descrição</Typography>
                      <Typography variant="body2">{selectedCharge.solicitacaoPagador}</Typography>
                    </Box>
                  )}

                  {selectedCharge.devedor && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Devedor</Typography>
                      <Typography variant="body2">
                        {selectedCharge.devedor.nome || '-'}
                        {selectedCharge.devedor.cpf && ` (CPF: ${maskDocument(selectedCharge.devedor.cpf)})`}
                        {selectedCharge.devedor.cnpj && ` (CNPJ: ${maskDocument(undefined, selectedCharge.devedor.cnpj)})`}
                      </Typography>
                    </Box>
                  )}

                  {selectedCharge.calendario && (
                    <>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Data de Criação</Typography>
                        <Typography variant="body2">
                          {formatDate(selectedCharge.calendario.criacao)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Expiração</Typography>
                        <Typography variant="body2">
                          {formatExpiracao(selectedCharge.calendario.expiracao)}
                        </Typography>
                      </Box>
                    </>
                  )}

                  {selectedCharge.revisao !== undefined && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Revisão</Typography>
                      <Typography variant="body2">{selectedCharge.revisao}</Typography>
                    </Box>
                  )}

                  {selectedCharge.location && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Location</Typography>
                      <Typography variant="body2" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>
                        {selectedCharge.location}
                      </Typography>
                    </Box>
                  )}

                  {selectedCharge.infoAdicionais && selectedCharge.infoAdicionais.length > 0 && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Informações Adicionais</Typography>
                      {selectedCharge.infoAdicionais.map((info, idx) => (
                        <Typography key={idx} variant="body2">
                          {info.nome}: {info.valor}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Stack>
              </Grid>

              <Grid item xs={12} md={6}>
                {(selectedCharge.pixCopiaECola || selectedCharge.brcode) && selectedCharge.status === 'ATIVA' && (
                  <Paper
                    sx={{
                      p: 3,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      backgroundColor: '#fff',
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    }}
                  >
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                      QR Code para Pagamento
                    </Typography>
                    <Box sx={{ p: 2, backgroundColor: '#fff', borderRadius: 1 }}>
                      <QRCodeSVG
                        id="qr-code-svg"
                        value={selectedCharge.pixCopiaECola || selectedCharge.brcode || ''}
                        size={200}
                        level="M"
                        includeMargin
                      />
                    </Box>

                    <Stack direction="row" spacing={1} sx={{ mt: 2, width: '100%' }}>
                      <Button
                        variant="contained"
                        startIcon={copied ? <CheckCircle /> : <ContentCopy />}
                        onClick={handleCopyPixCode}
                        color={copied ? 'success' : 'primary'}
                        fullWidth
                        size="small"
                      >
                        {copied ? 'Copiado!' : 'Copiar Código'}
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<Download />}
                        onClick={handleDownloadQRCode}
                        fullWidth
                        size="small"
                      >
                        Baixar QR
                      </Button>
                    </Stack>

                    <Divider sx={{ my: 2, width: '100%' }} />

                    <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                      Código Copia e Cola
                    </Typography>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1,
                        mt: 1,
                        width: '100%',
                        maxHeight: 80,
                        overflow: 'auto',
                        backgroundColor: alpha(theme.palette.grey[500], 0.08),
                      }}
                    >
                      <Typography
                        variant="caption"
                        fontFamily="monospace"
                        sx={{ wordBreak: 'break-all' }}
                      >
                        {selectedCharge.pixCopiaECola || selectedCharge.brcode}
                      </Typography>
                    </Paper>
                  </Paper>
                )}

                {selectedCharge.status !== 'ATIVA' && (
                  <Paper
                    sx={{
                      p: 3,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      backgroundColor: alpha(theme.palette.grey[500], 0.08),
                    }}
                  >
                    <QrCode sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
                    <Typography color="text.secondary" textAlign="center">
                      QR Code não disponível para cobranças com status "{selectedCharge.status}"
                    </Typography>
                  </Paper>
                )}
              </Grid>
            </Grid>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          {selectedCharge?.status === 'ATIVA' && (
            <Button
              variant="outlined"
              color="error"
              startIcon={canceling ? <CircularProgress size={16} /> : <Cancel />}
              onClick={handleCancelCharge}
              disabled={canceling}
            >
              {canceling ? 'Cancelando...' : 'Cancelar Cobrança'}
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button variant="contained" onClick={() => setDetailsOpen(false)}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PixHistoryPage;
